import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { CompanyDTO } from './useCompany';
import config from '@/config';

interface ApiError {
  message: string;
}

interface ApiResponse {
  success: boolean;
  data: {
    companies: CompanyDTO[];
  };
}

/**
 * React Query hook to fetch the company associated with a supplier.
 *
 * IMPORTANT: This is used for temporary currency handling.
 * The company's metadata.defaultCurrency is used to pre-populate currency
 * in CSV transformation workflows.
 *
 * TODO: Future refactoring should implement proper multi-currency support.
 * @see packages/shared-types/src/currency.ts for currency constants
 */
export const useCompanyBySupplier = (
  supplierId: string | undefined,
  enabled = true,
): UseQueryResult<CompanyDTO | null, ApiError> => {
  return useQuery<CompanyDTO | null, ApiError>({
    queryKey: ['company', 'by-supplier', supplierId],
    queryFn: async (): Promise<CompanyDTO | null> => {
      if (!supplierId) return null;

      const queryParams = new URLSearchParams();
      queryParams.append('supplierId', supplierId);
      queryParams.append('first', '1'); // We only expect one company per supplier

      const url = `${
        config.apiBaseUrl
      }/core/companies?${queryParams.toString()}`;

      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        const errorResponse: ApiError = await response.json();
        throw new Error(errorResponse.message);
      }

      const result: ApiResponse = await response.json();

      // Return first company or null if not found
      return result.data.companies[0] || null;
    },
    enabled: enabled && !!supplierId,
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};
